import { createAction } from '@reduxjs/toolkit';
import EcomEngineAPI from 'api/ecom_engine_api';
import { trackError } from 'utils/helpers';
import {
  addCacheKey,
  DATE_RANGE_BOOKABLILITY,
  isKeyExist,
  UNAVAILABLE_DATES,
} from 'utils/helpers/cached_api_parameter_manager';

const fetchAvailabilityStarted = createAction(
  'availabiltiy/fetchAvailabilityStarted'
);
const fetchAvailabilitySucceeded = createAction(
  'availabiltiy/fetchAvailabilitySucceeded'
);
const fetchAvailabilityFailure = createAction(
  'availabiltiy/fetchAvailabilityFailure'
);

const setAvailability = createAction('resources/setAvailability');

const fetchAvailability =
  ({ resourceIDs, fromDate, toDate }) =>
  (dispatch) => {
    const paramCopy = { resourceIDs, fromDate, toDate };
    if (isKeyExist(UNAVAILABLE_DATES, paramCopy)) {
      return () => {};
    }
    // Fetch the actual availabiltiy
    dispatch(fetchAvailabilityStarted());

    return EcomEngineAPI.availability
      .unavailableDates({ resourceIDs, fromDate, toDate })
      .then((json) => {
        dispatch(fetchAvailabilitySucceeded());
        dispatch(setAvailability(json));
        addCacheKey(UNAVAILABLE_DATES, paramCopy);
      })
      .catch((error) => {
        trackError(error);
        dispatch(fetchAvailabilityFailure(error));
      });
  };

const fetchPricesStarted = createAction('availabiltiy/fetchPricesStarted');
const fetchPricesSucceeded = createAction('availabiltiy/fetchPricesSucceeded');
const fetchPricesFailure = createAction('availabiltiy/fetchPricesFailure');

const setPrices = createAction('resources/setPrices');

const fetchPrices =
  ({
    availableDates,
    productTypeId,
    resourceID,
    passengerTypes,
    vehicleTypes,
    signal,
  }) =>
  (dispatch) => {
    const pricing = [];
    const params = {
      product_type_id: productTypeId,
      route_id: resourceID,
      forward: {
        first_travel_date: '2022-01-01',
        trip_id: 7,
        passenger_types: passengerTypes,
        vehicle_types: vehicleTypes,
      },
    };

    const getPriceEachDate = ({ travelDate }) => {
      params.forward.first_travel_date = travelDate;
      return EcomEngineAPI.reservation
        .findServices(params, signal)
        .then((json) => {
          const sortedPrices = json.sort(
            (a, b) => a.price_in_cents - b.price_in_cents
          ); // Maybe add discount later
          pricing.push(sortedPrices[0]);
        });
    };

    dispatch(fetchPricesStarted());

    const allPricePromise = availableDates.map((travelDate) =>
      getPriceEachDate({ travelDate })
    );

    return Promise.allSettled(allPricePromise)
      .then((status) => {
        const failedDates = [];
        status.forEach((eachStatus, index) => {
          if (eachStatus.status === 'rejected') {
            failedDates.push(availableDates[index]);
          }
        });

        if (failedDates.length) {
          // Do request once more for failed Dates
          dispatch(fetchPricesStarted());
          const failedDatesPromise = failedDates.map((travelDate) =>
            getPriceEachDate({ travelDate })
          );
          Promise.allSettled(failedDatesPromise)
            .then(() => {
              dispatch(fetchPricesSucceeded());
              dispatch(setPrices(pricing));
            })
            .catch((error) => {
              trackError(error);
              dispatch(fetchPricesFailure(error.message));
            });
        } else {
          dispatch(fetchPricesSucceeded());
          dispatch(setPrices(pricing));
        }
      })
      .catch((error) => {
        trackError(error);
        dispatch(fetchPricesFailure(error.message));
      });
  };

const fetchBookabilityStarted = createAction(
  'availabiltiy/fetchBookabilityStarted'
);
const fetchBookabilitySucceeded = createAction(
  'availabiltiy/fetchBookabilitySucceeded'
);
const fetchBookabilityFailure = createAction(
  'availabiltiy/fetchBookabilityFailure'
);

const setBookability = createAction('resources/setBookability');

const fetchBookability = ({
  resourceIds,
  passengerTypeNumbers,
  fromDate,
  toDate,
  pricingSection,
  updateFetchStatus,
}) => {
  const paramCopy = {
    resourceIds,
    passengerTypeNumbers,
    fromDate,
    toDate,
    pricingSection,
    updateFetchStatus,
  };
  const previousReturn = isKeyExist(DATE_RANGE_BOOKABLILITY, paramCopy);
  if (previousReturn) {
    return (dispatch) => {
      dispatch(setBookability(previousReturn));
    };
  }

  const params = { travelDate: fromDate, resourceIds, passengerTypeNumbers };

  return (dispatch) => {
    // Fetch the actual bookability
    if (updateFetchStatus) {
      dispatch(fetchBookabilityStarted(params));
    }

    return EcomEngineAPI.availability
      .dateRangeBookability({
        fromDate,
        toDate: toDate || fromDate,
        resourceIds,
        passengerTypeNumbers,
      })
      .then((json) => {
        if (updateFetchStatus) {
          dispatch(fetchBookabilitySucceeded(params));
        }
        const temp = { json, pricingSection };
        dispatch(setBookability(temp));
        addCacheKey(DATE_RANGE_BOOKABLILITY, paramCopy, temp);
      })
      .catch((error) => {
        trackError(error);

        if (updateFetchStatus) {
          dispatch(
            fetchBookabilityFailure({ ...params, error: error.message })
          );
        }
      });
  };
};

const PRICING_SECTION = {
  calendar: 'calendar',
  passenger: 'passenger',
};

export {
  fetchAvailabilityStarted,
  fetchAvailabilitySucceeded,
  fetchAvailabilityFailure,
  setAvailability,
  fetchAvailability,
  fetchPrices,
  fetchPricesStarted,
  fetchPricesSucceeded,
  fetchPricesFailure,
  setPrices,
  fetchBookabilityStarted,
  fetchBookabilitySucceeded,
  fetchBookabilityFailure,
  setBookability,
  fetchBookability,
  PRICING_SECTION,
};
